import { useAuthStore } from '@feature/authentication/hooks/useAuthStore'
import { useLogout } from '@feature/authentication/hooks/useLogout'
import { usePermissions } from '@feature/authentication/hooks/usePermissions'
import { NotificationsDropdown } from '@feature/notifications/components/NotificationsDropdown/NotificationsDropdown'
import { GlobalSearchBar } from '@feature/search/components/GlobalSearchBar/GlobalSearchBar'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Button } from '@shared/components/ui/button'
import { getUserRoleLabel } from '@shared/types/global.types'
import { LogOut, Menu, Moon, Search, Sun, User } from 'lucide-react'
import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Breadcrumb } from './Breadcrumb'

interface HeaderProps {
  onMenuClick: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user } = useAuthStore()
  const { isAdmin, isManager } = usePermissions()
  const { mutate: logout } = useLogout()
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const userInitials =
    (user?.name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U'

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex h-16 w-full max-w-[1680px] items-center gap-3 px-4 md:px-6 2xl:px-10">
        <div className="flex min-w-0 flex-1 items-center gap-2 md:flex-initial">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="shrink-0 lg:hidden"
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="hidden h-10 w-px bg-border/80 lg:block" />
          <div className="min-w-0 overflow-hidden">
            <Breadcrumb />
          </div>
        </div>

        <div className="hidden flex-1 md:block">{isAdmin && <GlobalSearchBar />}</div>

        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
          <Link
            to={isAdmin ? '/search' : isManager ? '/communications' : '/board'}
            aria-label="Buscar"
            className="rounded-full p-2 md:hidden"
          >
            <Search className="size-4" />
          </Link>
          <NotificationsDropdown />

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            title={`Mudar para tema ${resolvedTheme === 'light' ? 'escuro' : 'claro'}`}
            className="hidden rounded-full border border-transparent hover:border-border/80 hover:bg-accent/70 sm:inline-flex"
          >
            {mounted && resolvedTheme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          <Link
            to="/profile"
            aria-label="Meu Perfil"
            className="hidden max-w-64 items-center gap-3 rounded-full border border-border/80 bg-card/80 px-2 py-1.5 shadow-sm sm:flex"
          >
            <div className="flex items-center gap-2">
              <div
                aria-hidden="true"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
              >
                {userInitials}
              </div>
              <div className="min-w-0 text-sm">
                <p className="truncate font-medium">{user?.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {getUserRoleLabel(user?.role)}
                </p>
              </div>
            </div>
          </Link>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Menu da conta"
                className="rounded-full sm:hidden"
              >
                <span
                  aria-hidden="true"
                  className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
                >
                  {userInitials}
                </span>
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={8}
                className="z-50 min-w-48 rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg"
              >
                <DropdownMenu.Label className="px-3 py-2 text-xs text-muted-foreground">
                  {user?.name}
                  <span className="block">{getUserRoleLabel(user?.role)}</span>
                </DropdownMenu.Label>
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
                <DropdownMenu.Item asChild>
                  <Link
                    to="/profile"
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-accent"
                  >
                    <User className="size-4" />
                    Meu perfil
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={toggleTheme}
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-accent"
                >
                  {mounted && resolvedTheme === 'dark' ? (
                    <Sun className="size-4" />
                  ) : (
                    <Moon className="size-4" />
                  )}
                  {mounted && resolvedTheme === 'dark' ? 'Tema claro' : 'Tema escuro'}
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={() => logout()}
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-700 outline-none data-[highlighted]:bg-accent dark:text-red-300"
                >
                  <LogOut className="size-4" />
                  Sair
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => logout()}
            title="Sair"
            aria-label="Sair"
            className="hidden rounded-full border border-transparent hover:border-border/80 hover:bg-accent/70 sm:inline-flex"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  )
}
